import { Injectable } from '@angular/core';

export interface TestTimerState {
  testId: number;
  /** Session serveur à laquelle l'état se rapporte. */
  sessionId?: number | null;
  /** Faux pour un test sans limite : timeRemaining vaut alors 0 sans être écoulé. */
  hasTimeLimit?: boolean;
  /**
   * Questions dont la réponse n'a pas encore été confirmée par le serveur.
   * Absent d'un état écrit avant l'enregistrement en cours de test : rien n'y
   * était envoyé, tout y est donc à reprendre.
   */
  nonSynchronisees?: number[];
  /** Secondes passées écran du test ouvert, cumulées d'une ouverture à l'autre. */
  tempsActif?: number;
  startTime: number;
  timeRemaining: number;
  currentQuestionIndex: number;
  highestQuestionReached: number;
  answers: [number, number[]][];
  lastUpdated: number;
  isActive: boolean;
  visitedQuestions: number[]; // Track which questions were visited
  timedOutQuestions: number[]; // Track which questions timed out
}

@Injectable({
  providedIn: 'root',
})
export class TestTimerService {
  private readonly STORAGE_PREFIX = 'test_session_';
  private readonly ACTIVE_SESSION_KEY = 'active_test_session';
  private readonly MAX_SESSION_AGE = 8 * 60 * 60 * 1000; // 8 hours

  saveTestState(
    testId: number,
    state: Omit<TestTimerState, 'lastUpdated' | 'isActive'>,
  ): void {
    const key = this.getStorageKey(testId);
    const fullState: TestTimerState = {
      ...state,
      lastUpdated: Date.now(),
      isActive: true,
    };

    localStorage.setItem(key, JSON.stringify(fullState));
    localStorage.setItem(this.ACTIVE_SESSION_KEY, testId.toString());

    console.log('💾 Test state saved:', {
      testId,
      timeRemaining: fullState.timeRemaining,
      currentQuestion: fullState.currentQuestionIndex + 1,
      answersCount: fullState.answers.length,
    });
  }

  getTestState(testId: number): TestTimerState | null {
    const key = this.getStorageKey(testId);
    const data = localStorage.getItem(key);

    if (!data) {
      console.log('📭 No saved state found for test:', testId);
      return null;
    }

    try {
      const state = JSON.parse(data) as TestTimerState;

      // Validate session age
      const sessionAge = Date.now() - state.lastUpdated;
      if (sessionAge > this.MAX_SESSION_AGE) {
        console.log('⚠️ Session expired (older than 8 hours), clearing...');
        this.clearTestState(testId);
        return null;
      }

      // Check if session is marked as active
      const activeSessionId = localStorage.getItem(this.ACTIVE_SESSION_KEY);
      if (activeSessionId !== testId.toString()) {
        console.log('⚠️ Session is not active, clearing...');
        this.clearTestState(testId);
        return null;
      }

      console.log('📦 Restored test state:', {
        testId,
        sessionAge: Math.floor(sessionAge / 1000) + 's',
        timeRemaining: state.timeRemaining,
      });

      return state;
    } catch (error) {
      console.error('❌ Error parsing test state:', error);
      this.clearTestState(testId);
      return null;
    }
  }

  clearTestState(testId: number): void {
    const key = this.getStorageKey(testId);
    localStorage.removeItem(key);

    const activeSessionId = localStorage.getItem(this.ACTIVE_SESSION_KEY);
    if (activeSessionId === testId.toString()) {
      localStorage.removeItem(this.ACTIVE_SESSION_KEY);
    }

    console.log('🗑️ Test state cleared for test:', testId);
  }

  clearActiveSession(): void {
    const activeSessionId = localStorage.getItem(this.ACTIVE_SESSION_KEY);
    if (activeSessionId) {
      const testId = parseInt(activeSessionId, 10);
      if (!isNaN(testId)) {
        this.clearTestState(testId);
      }
    }
    console.log('🗑️ Active session cleared');
  }

  hasActiveSession(testId: number): boolean {
    const activeSessionId = localStorage.getItem(this.ACTIVE_SESSION_KEY);
    return activeSessionId === testId.toString();
  }

  getActiveSessionId(): number | null {
    const activeSessionId = localStorage.getItem(this.ACTIVE_SESSION_KEY);
    if (activeSessionId) {
      const testId = parseInt(activeSessionId, 10);
      return isNaN(testId) ? null : testId;
    }
    return null;
  }

  private getStorageKey(testId: number): string {
    return `${this.STORAGE_PREFIX}${testId}`;
  }

  /**
   * Calculate adjusted time remaining based on elapsed time since last save
   * Accounts for time spent while the page was closed/inactive
   */
  calculateAdjustedTimeRemaining(state: TestTimerState): number {
    const elapsedSinceLastUpdate = Math.floor(
      (Date.now() - state.lastUpdated) / 1000,
    );
    const adjustedTime = state.timeRemaining - elapsedSinceLastUpdate;

    console.log('⏱️ Time adjustment:', {
      savedTimeRemaining: state.timeRemaining,
      elapsedSeconds: elapsedSinceLastUpdate,
      adjustedTime: Math.max(0, adjustedTime),
    });

    return Math.max(0, adjustedTime);
  }
}
